import { useCallback, useMemo, useState } from 'react';

import type { MenuProps } from 'antd';

const SNOOZED_UNTIL_SESSION_KEY = 'socialIntegrationsWarningSnoozedUntil';
const NEVER_SHOW_STORAGE_KEY = 'socialIntegrationsWarningNeverShow';

const HOUR_MS = 60 * 60 * 1000;

const SNOOZE_DURATIONS_MS = {
  hour: HOUR_MS,
  day: 24 * HOUR_MS,
  week: 7 * 24 * HOUR_MS,
} as const;

type SnoozeDurationKey = keyof typeof SNOOZE_DURATIONS_MS;

const isSnoozeDurationKey = (key: string): key is SnoozeDurationKey =>
  key in SNOOZE_DURATIONS_MS;

const readIsSnoozed = (): boolean => {
  if (localStorage.getItem(NEVER_SHOW_STORAGE_KEY) === '1') return true;

  const snoozedUntil = Number(
    sessionStorage.getItem(SNOOZED_UNTIL_SESSION_KEY)
  );

  return snoozedUntil > 0 && Date.now() < snoozedUntil;
};

const SNOOZE_MENU_ITEMS: MenuProps['items'] = [
  { key: 'hour', label: 'Скрыть на 1 час' },
  { key: 'day', label: 'Скрыть на 1 день' },
  { key: 'week', label: 'Скрыть на 1 неделю' },
  { type: 'divider' },
  { key: 'never', label: 'Больше не показывать', danger: true },
];

export const useSocialIntegrationsSnooze = () => {
  const [, setSnoozeRevision] = useState(0);

  const isSnoozed = readIsSnoozed();

  const snoozeMenuClickHandler = useCallback<NonNullable<MenuProps['onClick']>>(
    ({ key }) => {
      if (key === 'never') {
        localStorage.setItem(NEVER_SHOW_STORAGE_KEY, '1');
      } else if (isSnoozeDurationKey(key)) {
        sessionStorage.setItem(
          SNOOZED_UNTIL_SESSION_KEY,
          String(Date.now() + SNOOZE_DURATIONS_MS[key])
        );
      }

      setSnoozeRevision((revision) => revision + 1);
    },
    []
  );

  const snoozeMenu: MenuProps = useMemo(
    () => ({ items: SNOOZE_MENU_ITEMS, onClick: snoozeMenuClickHandler }),
    [snoozeMenuClickHandler]
  );

  return { isSnoozed, snoozeMenu };
};
